import React from "react";
import { SxProps, useTheme } from "@mui/material/styles";
import { CustomButton, CustomButtonText, ButtonSwitchContainer } from "./ButtonSwitch.styles";
import { SizeStyles, Variants } from "./ButtonSwitch.variants";


export interface CustomVariantButtonSwitchProps {
    variant?: "primary" | "secondary";
    value?: boolean | undefined;
    disabled?: boolean;
    error?: boolean;
    size?: "sm" | "md" | "lg";
    leftButtonChildren: React.ReactNode;
    middleButtonChildren?: React.ReactNode;
    rightButtonChildren: React.ReactNode;
    active?: "left" | "middle" | "right";
    onLeftButtonClick?: () => void;
    onMiddleButtonClick?: () => void;
    onRightButtonClick?: () => void;
    sx?: SxProps;
    leftButtonDataCy?: string;
    rightButtonDataCy?: string;
}

export const ButtonSwitch = ({
    variant = "primary",
    value = undefined,
    disabled = false,
    error = false,
    size = "md",
    leftButtonChildren = "Left",
    middleButtonChildren,
    rightButtonChildren = "Right",
    active,
    sx,
    onLeftButtonClick,
    onRightButtonClick,
    leftButtonDataCy,
    rightButtonDataCy,
    onMiddleButtonClick,
}: CustomVariantButtonSwitchProps) => {
    const theme = useTheme();
    
    const [activeButton, setActiveButton] = React.useState<"left" | "middle" | "right" | null>(active ?? null);

    const variantStylesFn = Variants(theme);
    const sizeStylesFn = SizeStyles(theme);

    const isSelected = (position: "left" | "middle" | "right") => {
        if (typeof value !== "undefined") {
            if (position === "left") return value === true;
            if (position === "right") return value === false;
            return false;
        }
        return activeButton === position;
    };

    return (
        <ButtonSwitchContainer
            sx={{
                ...variantStylesFn[variant],
                ...sx,
                border: error ? '1px solid red' : 'none',
                pointerEvents: disabled ? 'none' : 'auto'
            }}
        >
            <CustomButton
                sx={{
                    ...sizeStylesFn[size],
                }}
                className={`left${isSelected("left") ? ' is-Selected' : ''}`}
                onClick={() => {
                    setActiveButton("left");
                    if (onLeftButtonClick) {
                        onLeftButtonClick();
                    }
                }}
                data-cy={leftButtonDataCy}
            >
                <CustomButtonText>
                    {leftButtonChildren}
                </CustomButtonText>
            </CustomButton>
            {middleButtonChildren && (
                <CustomButton
                    sx={{
                        ...sizeStylesFn[size],
                    }}
                    className={`middle${isSelected("middle") ? ' is-Selected' : ''}`}
                    onClick={() => {
                        setActiveButton("middle");
                        if (onMiddleButtonClick) {
                            onMiddleButtonClick();
                        }
                    }}
                >
                    <CustomButtonText>
                        {middleButtonChildren}
                    </CustomButtonText>
                </CustomButton>
            )}
            <CustomButton
                sx={{
                    ...sizeStylesFn[size],
                }}
                className={`right${isSelected("right") ? ' is-Selected' : ''}`}
                onClick={() => {
                    setActiveButton("right");
                    if (onRightButtonClick){
                        onRightButtonClick();
                    }
                }}
                data-cy={rightButtonDataCy}
            >
                <CustomButtonText>
                    {rightButtonChildren}
                </CustomButtonText>
            </CustomButton>
        </ButtonSwitchContainer>
    );
};

export interface ButtonSwitchProps extends React.ComponentProps<typeof ButtonSwitch> {
    as?: React.ElementType;
}

ButtonSwitch.displayName = "ButtonSwitch";
